import React from 'react';
import { View, StyleSheet, Image } from 'react-native';
import { Text, Card, Chip, useTheme } from 'react-native-paper';

interface RecipePreviewProps {
  data: any;
}

export function RecipePreview({ data }: RecipePreviewProps) {
  const theme = useTheme();

  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}min`
      : `${hours}h`;
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <Card style={styles.card}>
        <Card.Content>
          <Text variant='headlineMedium' style={styles.title}>
            {data.title || 'Recipe Title'}
          </Text>

          <Text
            variant='bodyMedium'
            style={[
              styles.description,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            {data.description || 'Recipe description will appear here...'}
          </Text>

          <View style={styles.metadata}>
            <Chip mode='outlined' compact>
              {data.brewing_method?.replace(/_/g, ' ') || 'Pour Over'}
            </Chip>
            <Chip mode='outlined' compact>
              {data.difficulty || 'Beginner'}
            </Chip>
            <Chip mode='outlined' compact>
              {formatTime(data.total_time_minutes || 10)}
            </Chip>
            <Chip mode='outlined' compact>
              {data.servings || 1} serving{(data.servings || 1) > 1 ? 's' : ''}
            </Chip>
          </View>
        </Card.Content>
      </Card>

      {/* Main Image */}
      {data.images && data.images.length > 0 && (
        <Card style={styles.card}>
          <Card.Cover
            source={{ uri: data.images[0] }}
            style={styles.mainImage}
          />
        </Card>
      )}

      {/* Brewing Parameters */}
      <Card style={styles.card}>
        <Card.Content>
          <Text variant='titleLarge' style={styles.sectionTitle}>
            Brewing Parameters
          </Text>

          <View style={styles.parameterGrid}>
            <View style={styles.parameterItem}>
              <Text
                variant='bodySmall'
                style={[
                  styles.parameterLabel,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Grind Size
              </Text>
              <Text variant='bodyMedium'>
                {data.brewing_parameters?.grind_size?.replace(/_/g, ' ') ||
                  'Medium'}
              </Text>
            </View>

            <View style={styles.parameterItem}>
              <Text
                variant='bodySmall'
                style={[
                  styles.parameterLabel,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Water Temp
              </Text>
              <Text variant='bodyMedium'>
                {data.brewing_parameters?.water_temperature_celsius || 85}°C
              </Text>
            </View>

            <View style={styles.parameterItem}>
              <Text
                variant='bodySmall'
                style={[
                  styles.parameterLabel,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Ratio
              </Text>
              <Text variant='bodyMedium'>
                {data.brewing_parameters?.coffee_to_water_ratio || '1:15'}
              </Text>
            </View>

            <View style={styles.parameterItem}>
              <Text
                variant='bodySmall'
                style={[
                  styles.parameterLabel,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Brew Time
              </Text>
              <Text variant='bodyMedium'>
                {formatDuration(
                  data.brewing_parameters?.total_brew_time_seconds
                ) || '4m'}
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Ingredients */}
      <Card style={styles.card}>
        <Card.Content>
          <Text variant='titleLarge' style={styles.sectionTitle}>
            Ingredients
          </Text>

          {data.ingredients && data.ingredients.length > 0 ? (
            data.ingredients.map((ingredient: any, index: number) => (
              <View key={index} style={styles.ingredientItem}>
                <View style={styles.ingredientAmount}>
                  <Text variant='titleMedium'>
                    {ingredient.amount} {ingredient.unit}
                  </Text>
                </View>
                <View style={styles.ingredientDetails}>
                  <Text variant='bodyMedium'>{ingredient.name}</Text>
                  {ingredient.notes ? (
                    <Text
                      variant='bodySmall'
                      style={{ color: theme.colors.onSurfaceVariant }}
                    >
                      {ingredient.notes}
                    </Text>
                  ) : null}
                </View>
              </View>
            ))
          ) : (
            <Text
              variant='bodyMedium'
              style={{ color: theme.colors.onSurfaceVariant }}
            >
              No ingredients added yet
            </Text>
          )}
        </Card.Content>
      </Card>

      {/* Steps */}
      <Card style={styles.card}>
        <Card.Content>
          <Text variant='titleLarge' style={styles.sectionTitle}>
            Instructions
          </Text>

          {data.steps && data.steps.length > 0 ? (
            data.steps.map((step: any, index: number) => (
              <View key={index} style={styles.stepItem}>
                <View
                  style={[
                    styles.stepNumber,
                    { backgroundColor: theme.colors.primary },
                  ]}
                >
                  <Text
                    variant='labelMedium'
                    style={{ color: theme.colors.onPrimary }}
                  >
                    {index + 1}
                  </Text>
                </View>

                <View style={styles.stepContent}>
                  {step.title && (
                    <Text variant='titleMedium' style={styles.stepTitle}>
                      {step.title}
                    </Text>
                  )}

                  <Text variant='bodyMedium' style={styles.stepDescription}>
                    {step.description || 'Step description...'}
                  </Text>

                  {(step.duration_seconds || step.temperature_celsius) && (
                    <View style={styles.stepMeta}>
                      {step.duration_seconds && (
                        <Chip mode='outlined' compact style={styles.stepChip}>
                          {formatDuration(step.duration_seconds)}
                        </Chip>
                      )}
                      {step.temperature_celsius && (
                        <Chip mode='outlined' compact style={styles.stepChip}>
                          {step.temperature_celsius}°C
                        </Chip>
                      )}
                    </View>
                  )}

                  {step.notes && (
                    <Text
                      variant='bodySmall'
                      style={[
                        styles.stepNotes,
                        { color: theme.colors.onSurfaceVariant },
                      ]}
                    >
                      💡 {step.notes}
                    </Text>
                  )}

                  {step.image_url && (
                    <Image
                      source={{ uri: step.image_url }}
                      style={styles.stepImage}
                    />
                  )}
                </View>
              </View>
            ))
          ) : (
            <Text
              variant='bodyMedium'
              style={{ color: theme.colors.onSurfaceVariant }}
            >
              No steps added yet
            </Text>
          )}
        </Card.Content>
      </Card>

      {/* Tags */}
      {data.tags && data.tags.length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Tags
            </Text>

            <View style={styles.tagsContainer}>
              {data.tags.map((tag: string, index: number) => (
                <Chip key={index} mode='outlined' compact style={styles.tag}>
                  {tag}
                </Chip>
              ))}
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Video */}
      {data.video_url && (
        <Card style={styles.card}>
          <Card.Content>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Video
            </Text>

            <View style={styles.videoPlaceholder}>
              <Text
                variant='bodyMedium'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                🎥 Video will be embedded here
              </Text>
              <Text
                variant='bodySmall'
                style={[styles.videoUrl, { color: theme.colors.primary }]}
              >
                {data.video_url}
              </Text>
            </View>
          </Card.Content>
        </Card>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  card: {
    marginBottom: 16,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  description: {
    marginBottom: 16,
    lineHeight: 20,
  },
  metadata: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  mainImage: {
    height: 200,
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: 16,
  },
  parameterGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  parameterItem: {
    flex: 1,
    minWidth: '45%',
  },
  parameterLabel: {
    marginBottom: 4,
  },
  ingredientItem: {
    flexDirection: 'row',
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  ingredientAmount: {
    width: 80,
    marginRight: 16,
  },
  ingredientDetails: {
    flex: 1,
  },
  stepItem: {
    flexDirection: 'row',
    marginBottom: 20,
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    marginTop: 4,
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  stepDescription: {
    marginBottom: 8,
    lineHeight: 20,
  },
  stepMeta: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  stepChip: {
    height: 28,
  },
  stepNotes: {
    marginBottom: 8,
    fontStyle: 'italic',
  },
  stepImage: {
    width: '100%',
    height: 150,
    borderRadius: 8,
    marginTop: 8,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    marginBottom: 4,
  },
  videoPlaceholder: {
    alignItems: 'center',
    paddingVertical: 32,
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: 8,
  },
  videoUrl: {
    marginTop: 8,
    textAlign: 'center',
  },
});
